import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { Button, Col, Input, List, Modal, notification, Row } from 'antd'
import './ChangeExpertStatusModal.css'

import ExpertStatusSelect from '../../../helpers/ExpertStatusSelect/ExpertStatusSelect'
import SpinLoader from '../../../helpers/SpinLoader'

import { User, TExpert, IUser, Statuses, TStatuses } from '../../../../context/types'
import { callApi, TApiResponse } from '../../../../context/api'
import Paragraph from 'antd/es/typography/Paragraph'
import { useAuthContext } from '../../../../context/auth'
import { useExpertProfilesContext } from '../../../../context/expertProfile'

const { TextArea } = Input

interface ChangeExpertStatusModalProps {
	isVisible: boolean
	onChange: (value: boolean) => void
	onSave?: (isUpdated: boolean) => void
	forceUpdate?: () => void
	user: User | IUser
}

const ChangeExpertStatusModal: FunctionComponent<ChangeExpertStatusModalProps> = (props) => {
	const { isVisible, onChange, user, onSave } = props
	const { auth } = useAuthContext()
	const { expertProfiles, actions } = useExpertProfilesContext()
	const expertFullName = `${user.first_name} ${user.last_name}`

	const [updatedExperts, setUpdatedExperts] = useState<TExpert[]>([])
	const [loading, setLoading] = useState<boolean>(false)
	const [experts, setExperts] = useState<TExpert[]>([])
	const [quarantineReason, setQuarantineReason] = useState<string>('')
	const [newStatus, setNewStatus] = useState<keyof TStatuses>(null)

	const fetchExperts = async () => {
		try {
			setLoading(true)
			setNewStatus(null)
			const profilesResponse: TApiResponse = await callApi(`/experts/user/${user.id}`, {})

			const { data } = profilesResponse.payload

			if (profilesResponse.ok) {
				setExperts(data.experts)
			} else {
				throw new Error(data.message || 'Cannot get experts!')
			}

			setLoading(false)
		} catch (e) {
			notification.error({ message: e.message })
		}
	}
	const fetchUpdateProfiles = async () => {
		let isUpdated = false
		const filteredExperts = updatedExperts.filter(
			(expert, index) => expert.profile.status !== experts[index].profile.status
		)

		if (filteredExperts.length) {
			try {
				setLoading(true)
				const url = auth.user.is_admin ? 'admin/expert/profiles' : 'experts/profiles'

				const body: { experts: TExpert[]; quarantine_reason?: string } = {
					experts: filteredExperts,
				}
				if (quarantineReason && newStatus === Statuses.QUARANTINED) {
					body.quarantine_reason = quarantineReason
				}
				const updateProfilesResponse: TApiResponse = await callApi(url, {
					method: 'PUT',
					body: body,
				})
				isUpdated = true

				const newProfiles = expertProfiles.profiles.map((p) => {
					const profile = filteredExperts.find((expert) => expert.profile.id === p.id)?.profile
					if (profile) {
						return {
							...p,
							status: profile.status,
						}
					}
					return p
				})
				actions.setProfiles(newProfiles)

				const { data } = updateProfilesResponse.payload

				if (updateProfilesResponse.ok) {
					notification.success({ message: 'All profiles were updated' })
					onChange(false)

					if (quarantineReason) {
						setQuarantineReason('')
					}
				} else {
					throw new Error(data.message || 'Cannot update user profiles!')
				}
			} catch (e) {
				notification.error({ message: e.message })
			}

			setLoading(false)
		} else {
			onChange(false)
		}
		onSave?.(isUpdated)
	}

	const loadExperts = useCallback(fetchExperts, [user])

	useEffect(() => {
		if (isVisible) {
			loadExperts()
		}
	}, [isVisible])

	useEffect(() => {
		if (experts.length && !updatedExperts.length) {
			setUpdatedExperts([...experts])
		}
	}, [experts])

	const onChangeSelect = (newExpert: TExpert, status: keyof TStatuses) => {
		const changedExperts = updatedExperts.map((expert) => {
			return expert.id === newExpert.id ? newExpert : expert
		})

		setUpdatedExperts(changedExperts)
		setNewStatus(status)
	}

	return (
		<Modal
			title={expertFullName}
			visible={isVisible}
			onCancel={() => onChange(false)}
			footer={[
				<Button key="back" onClick={() => onChange(false)}>
					Cancel
				</Button>,
				<Button
					disabled={newStatus === null}
					key="submit"
					type="primary"
					loading={loading}
					onClick={fetchUpdateProfiles}
				>
					Save
				</Button>,
			]}
		>
			<SpinLoader loading={!!loading}>
				<List
					dataSource={experts}
					renderItem={(expert) => (
						<List.Item>
							<Row gutter={20}>
								<Col xs={24} md={12}>
									<Paragraph>
										<span>Brand: {expert.profile.brand.name}</span>
									</Paragraph>
									<span>{expert.title}</span>
								</Col>

								<Col xs={24} md={12}>
									<ExpertStatusSelect onChange={onChangeSelect} expert={expert} />
								</Col>
							</Row>
							{newStatus === Statuses.QUARANTINED && (
								<div className="quarantine-reason">
									<Paragraph>
										Please let the expert know why their profile is being placed in quarantine status
									</Paragraph>
									<TextArea
										placeholder="Quarantine reason"
										value={quarantineReason}
										onChange={(e) => setQuarantineReason(e.target.value)}
									/>
								</div>
							)}
						</List.Item>
					)}
				/>
			</SpinLoader>
		</Modal>
	)
}

export default ChangeExpertStatusModal
